import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/axios";
import "./KeysPage.css";

interface Row {
  id: number;
  name: string;
  sku: string | null;
  isActive: boolean;
  licenseType: string | null;
  price: number | null;
  hasLink: boolean;
  available: number;
  used: number;
  lastAddedAt: string | null;
  lastUsedAt: string | null;
}

interface KeyRow {
  id: number;
  keyValue: string;
  status: "available" | "used";
  dealId: number | null;
  dealNumber: string | null;
  clientName: string | null;
  usedAt: string | null;
  createdAt: string;
}

interface Detail {
  product: { id: number; name: string; nameKz: string | null; sku: string | null; isActive: boolean; licenseType: string | null; price: number | null; downloadLink: string | null; isMont: boolean };
  stats: { available: number; used: number };
  keys: KeyRow[];
}

interface LogRow {
  id: number;
  usedAt: string | null;
  dealId: number | null;
  dealNumber: string | null;
  productId: number;
  name: string;
  keyValue: string;
}

const LOW = 3;
const money = (n: number | null) => (n == null ? "—" : new Intl.NumberFormat("ru-RU").format(n) + " ₸");
const when = (s: string | null) =>
  s ? new Date(s).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

function state(r: Pick<Row, "available">): "none" | "low" | "ok" {
  return r.available <= 0 ? "none" : r.available <= LOW ? "low" : "ok";
}
const STATE_LABEL = { none: "Нет ключей", low: "Заканчиваются", ok: "В наличии" } as const;

export default function KeysPage() {
  const [params, setParams] = useSearchParams();
  const selected = Number(params.get("product")) || null;
  const [view, setView] = useState<"stock" | "log">("stock");

  const [rows, setRows] = useState<Row[] | null>(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "low" | "none">("all");
  const [log, setLog] = useState<LogRow[] | null>(null);

  const loadRows = useCallback(() => {
    api.get<Row[]>("/product-keys/overview").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(loadRows, [loadRows]);
  useEffect(() => {
    if (view === "log") api.get<LogRow[]>("/product-keys/log/issued").then((r) => setLog(r.data)).catch(() => setLog([]));
  }, [view]);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows ?? [])
      .filter((r) => !s || r.name.toLowerCase().includes(s) || (r.sku ?? "").toLowerCase().includes(s))
      .filter((r) => filter === "all" || (filter === "none" ? r.available <= 0 : r.available > 0 && r.available <= LOW))
      .sort((a, b) => a.available - b.available || a.name.localeCompare(b.name, "ru"));
  }, [rows, q, filter]);

  const totals = useMemo(() => {
    const r = rows ?? [];
    return {
      products: r.length,
      available: r.reduce((s, x) => s + x.available, 0),
      used: r.reduce((s, x) => s + x.used, 0),
      low: r.filter((x) => x.available > 0 && x.available <= LOW).length,
      none: r.filter((x) => x.available <= 0).length,
    };
  }, [rows]);

  const open = (id: number | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set("product", String(id));
    else p.delete("product");
    setParams(p, { replace: false });
  };

  return (
    <div className={`keys-page ${selected ? "has-detail" : ""}`}>
      <div className="keys-head">
        <div>
          <h1>Ключи</h1>
          <div className="keys-sub">Склад ключей программ. Ключи MONT WebStore здесь не хранятся — их выдаёт MONT при заказе.</div>
        </div>
        <div className="keys-tabs">
          <button className={view === "stock" ? "on" : ""} onClick={() => setView("stock")}>Остатки</button>
          <button className={view === "log" ? "on" : ""} onClick={() => setView("log")}>Журнал выдачи</button>
        </div>
      </div>

      {view === "log" ? (
        <div className="keys-card">
          {log === null && <div className="keys-empty">Загрузка…</div>}
          {log && log.length === 0 && <div className="keys-empty">Выдач пока не было</div>}
          {(log ?? []).map((l) => (
            <div key={l.id} className="keys-logrow">
              <div className="when">{when(l.usedAt)}</div>
              <div className="what">
                <b>{l.name}</b>
                <span className="mono">{l.keyValue}</span>
              </div>
              <div className="deal">{l.dealNumber ?? (l.dealId ? `#${l.dealId}` : "—")}</div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="keys-stats">
            <div><b>{totals.available}</b><span>свободных ключей</span></div>
            <div><b>{totals.used}</b><span>выдано</span></div>
            <div className={totals.low ? "warn" : ""}><b>{totals.low}</b><span>заканчиваются (≤ {LOW})</span></div>
            <div className={totals.none ? "bad" : ""}><b>{totals.none}</b><span>без ключей</span></div>
          </div>

          <div className="keys-layout">
            <section className="keys-list">
              <div className="keys-tools">
                <input placeholder="Поиск по названию или артикулу" value={q} onChange={(e) => setQ(e.target.value)} />
                <div className="chips">
                  {([["all", "Все"], ["low", "Заканчиваются"], ["none", "Нет ключей"]] as const).map(([k, l]) => (
                    <button key={k} className={filter === k ? "on" : ""} onClick={() => setFilter(k)}>{l}</button>
                  ))}
                </div>
              </div>
              {rows === null && <div className="keys-empty">Загрузка…</div>}
              {rows && list.length === 0 && <div className="keys-empty">Ничего не найдено</div>}
              {list.map((r) => {
                const st = state(r);
                return (
                  <button key={r.id} className={`keys-item ${selected === r.id ? "on" : ""}`} onClick={() => open(r.id)}>
                    <div className="top">
                      <span className="nm">{r.name}</span>
                      <span className={`badge ${st}`}>{STATE_LABEL[st]}</span>
                    </div>
                    <div className="sub">
                      <span>{r.sku ?? "без артикула"}</span>
                      {!r.isActive && <span className="off">снят с сайта</span>}
                    </div>
                    <div className="nums">
                      <span><b>{r.available}</b> свободно</span>
                      <span><b>{r.used}</b> выдано</span>
                    </div>
                  </button>
                );
              })}
            </section>

            <section className="keys-detail">
              {selected ? <DetailPanel key={selected} id={selected} onBack={() => open(null)} onChanged={loadRows} /> : (
                <div className="keys-empty big">Выберите программу слева — откроются ключи, пополнение и история выдачи</div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function DetailPanel({ id, onBack, onChanged }: { id: number; onBack: () => void; onChanged: () => void }) {
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState("");
  const [show, setShow] = useState<"available" | "used" | "all">("available");
  const [search, setSearch] = useState("");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ added: number; duplicates: string[] } | null>(null);
  const [copied, setCopied] = useState<number | "all" | null>(null);

  const load = useCallback(() => {
    api.get<Detail>(`/product-keys/product/${id}`).then((r) => { setD(r.data); setErr(""); }).catch((e) => setErr(e?.response?.data?.message || "Не удалось загрузить"));
  }, [id]);
  useEffect(load, [load]);

  const lines = useMemo(() => input.split(/\r?\n/).map((s) => s.trim()).filter(Boolean), [input]);
  const uniqueLines = useMemo(() => new Set(lines).size, [lines]);

  const add = async () => {
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const r = await api.post<{ added: number; duplicates: string[] }>(`/product-keys/product/${id}/add`, { keys: input });
      setResult(r.data);
      setInput("");
      load();
      onChanged();
    } catch (e: any) {
      const m = e?.response?.data?.message;
      setErr(Array.isArray(m) ? m.join(", ") : m || "Не удалось добавить");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (k: KeyRow) => {
    if (!window.confirm(`Удалить свободный ключ ${k.keyValue}?`)) return;
    try {
      await api.delete(`/product-keys/${k.id}`);
      load();
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.message || "Не удалось удалить");
    }
  };

  const copy = async (text: string, tag: number | "all") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      window.prompt("Скопируйте:", text);
    }
  };

  if (err && !d) return <div className="keys-empty big">{err}</div>;
  if (!d) return <div className="keys-empty big">Загрузка…</div>;

  const s = d.keys.filter((k) => (show === "all" || k.status === show) && (!search.trim() || k.keyValue.toLowerCase().includes(search.trim().toLowerCase()) || (k.dealNumber ?? "").toLowerCase().includes(search.trim().toLowerCase())));
  const st = state({ available: d.stats.available });

  return (
    <div className="kd">
      <button className="kd-back" onClick={onBack}>← К списку программ</button>
      <div className="kd-title">
        <h2>{d.product.name}</h2>
        <span className={`badge ${st}`}>{STATE_LABEL[st]}</span>
      </div>
      <div className="kd-info">
        <div><span>Артикул</span><b>{d.product.sku ?? "—"}</b></div>
        <div><span>Цена</span><b>{money(d.product.price)}</b></div>
        <div><span>Лицензия</span><b>{d.product.licenseType ?? "—"}</b></div>
        <div><span>На сайте</span><b>{d.product.isActive ? "продаётся" : "скрыт"}</b></div>
        <div className="wide"><span>Ссылка для скачивания</span><b>{d.product.downloadLink ? <a href={d.product.downloadLink} target="_blank" rel="noreferrer">{d.product.downloadLink}</a> : "не указана (редактируется в карточке товара)"}</b></div>
      </div>

      {d.product.isMont ? (
        <div className="kd-note">Это товар MONT WebStore: ключ заказывается у MONT автоматически при продаже, свой пул не используется.</div>
      ) : (
        <>
          <div className="kd-count">
            <div><b className="g">{d.stats.available}</b><span>свободно</span></div>
            <div><b>{d.stats.used}</b><span>выдано</span></div>
          </div>

          <div className="kd-add">
            <div className="kd-h">Пополнить ключи</div>
            <textarea rows={5} placeholder={"XXXXX-XXXXX-XXXXX-XXXXX\nYYYYY-YYYYY-YYYYY-YYYYY\n(по одному ключу в строке)"} value={input} onChange={(e) => setInput(e.target.value)} />
            <div className="kd-addrow">
              <span className="muted">
                {lines.length ? `Строк: ${lines.length}${uniqueLines !== lines.length ? `, повторов внутри списка: ${lines.length - uniqueLines}` : ""}` : "Вставьте ключи из письма или файла"}
              </span>
              <button className="kd-btn" disabled={busy || !lines.length} onClick={add}>{busy ? "Добавляем…" : "Добавить"}</button>
            </div>
            {result && (
              <div className="kd-result">
                ✓ Добавлено: <b>{result.added}</b>
                {result.duplicates.length > 0 && (
                  <div className="dups">Пропущено повторов: {result.duplicates.length} (такие ключи уже были){result.duplicates.slice(0, 5).map((x) => <div key={x} className="mono">{x}</div>)}</div>
                )}
              </div>
            )}
            {err && <div className="kd-err">{err}</div>}
          </div>

          <div className="kd-keys">
            <div className="kd-toolbar">
              <div className="chips">
                {([["available", "Свободные"], ["used", "Выданные"], ["all", "Все"]] as const).map(([k, l]) => (
                  <button key={k} className={show === k ? "on" : ""} onClick={() => setShow(k)}>{l}</button>
                ))}
              </div>
              <input placeholder="Найти ключ или сделку" value={search} onChange={(e) => setSearch(e.target.value)} />
              <button className="kd-link" onClick={() => copy(d.keys.filter((k) => k.status === "available").map((k) => k.keyValue).join("\n"), "all")} disabled={!d.stats.available}>
                {copied === "all" ? "Скопировано ✓" : "Скопировать свободные"}
              </button>
            </div>
            {s.length === 0 && <div className="keys-empty">Ключей нет</div>}
            {s.map((k) => (
              <div key={k.id} className={`kd-key ${k.status}`}>
                <span className={`kd-chip ${k.status}`}>{k.status === "available" ? "свободен" : "выдан"}</span>
                <span className="mono val">{k.keyValue}</span>
                <span className="meta">
                  {k.status === "used" ? `${k.dealNumber ?? `#${k.dealId}`}${k.clientName ? ` · ${k.clientName}` : ""} · ${when(k.usedAt)}` : `добавлен ${when(k.createdAt)}`}
                </span>
                <button className="ico" title="Скопировать" onClick={() => copy(k.keyValue, k.id)}>{copied === k.id ? "✓" : "⧉"}</button>
                {k.status === "available" && <button className="ico del" title="Удалить" onClick={() => remove(k)}>×</button>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
